<template>
  <div>
    <v-dialog
      v-model="dialog"
      width="916"
      open-on-focus
      no-click-animation
      persistent
      content-class="ml-file-picker-dialog"
      @click:outside="onClickOutside"
      @keydown="onKeydown"
    >
      <v-card elevation="0">
        <v-toolbar flat height="76">
          <v-toolbar-title class="ml-file-picker-toolbar-title"
            >Add from media library</v-toolbar-title
          >
          <v-spacer></v-spacer>
          <library-filters
            :value="searchBy.name"
            :currentFilter="currentFilter"
            @search-clear="searchClear"
            @search-on:input="searchInput"
            @search-on:run="searchRun"
            @filter="filterBy"
          />
          <v-btn color="accent" icon tile @click="closeDialog">
            <v-icon size="24">mdi-close</v-icon>
          </v-btn>
        </v-toolbar>
        <div class="library">
          <v-overlay :value="loading" z-index="4">
            <v-progress-circular indeterminate size="64"></v-progress-circular>
          </v-overlay>
          <library-breadcrumbs
            v-if="!useLibraryResources"
            :search="currentFilter.type == 'name' ? currentFilter.value : false"
            :breadcrumbs="breadcrumbsFormatted"
            @load-directory="loadDirectory"
            @clear-search="searchClear"
            @refresh-results="filterBy({ name: currentFilter.value })"
          ></library-breadcrumbs>
          <h5 v-if="folders.length > 0" class="library-heading">Folders</h5>
          <div class="library-folders">
            <library-folder
              v-for="folder in folders"
              :key="folder.id"
              :id="folder.id"
              :name="folder.name"
              :count="folder.recursiveFileCount"
              :version="folder.version"
              :locked="true"
              @load-directory="loadDirectory"
            />
          </div>
          <h5 v-if="files.length > 0" class="library-heading">Files</h5>
          <div class="library-files">
            <library-file
              v-for="file in files"
              :disable-all="
                allowedFileTypes !== null &&
                  !allowedFileTypes.includes(file.fileTypeId)
              "
              :key="file.id"
              :id="file.id"
              :name="file.title ? file.title : file.name"
              :thumbnailUrl="file.thumbnailUrl"
              :creationDate="file.creationDate"
              :createdByName="file.createdByName"
              :createdById="file.createdById"
              :version="file.version"
              :favoriteProfileId="file.favoriteProfileId"
              :locked="true"
              :labels="file.labels"
              :message-resource="resource ? resource : false"
              :get-resource="getResource"
              :fileTypeId="file.fileTypeId"
              :profileId="profileId"
              :processing="file.processing"
              :hoverLoader="progressBarsValues[file.id]"
              @mouseover.native="
                fileOpenHover(file.id, file.thumbnailUrl, file.previewUrl)
              "
              @mouseleave.native="fileClearHover(file.id)"
              @stopHoverPopup="
                fileClearHover(file.id)
                fileCloseHover()
              "
              @toggleFavorite="favoriteResource"
              @select-file="
                chooseFile({
                  ...file,
                  id: file.id,
                  name: file.title ? file.title : file.name,
                })
              "
              v-on="$listeners"
            />
          </div>
          <div class="library-pagination--container">
            <v-pagination
              v-if="pagination.totalPages > 1"
              circle
              :length="pagination.totalPages"
              :value="pagination.pageNumber + 1"
              class="library-pagination"
              @input="changeLibraryPage"
            />
          </div>
        </div>
      </v-card>
    </v-dialog>
    <v-dialog
      v-model="fileHover"
      hide-overlay
      content-class="ml-file-picker-dialog__file-hover-dialog"
      @click:outside="onClickOutsideFileHover"
      @keydown="onKeydownFileHover"
    >
      <v-card @mouseleave.native.stop="fileCloseHover()">
        <img
          v-if="fileHoverUrl && fileHoverType == 'base64'"
          :src="'data:image/jpeg;base64,' + fileHoverUrl"
        />
        <img
          v-else-if="fileHoverUrl && fileHoverType == 'url'"
          :src="fileHoverUrl"
        />
        <v-btn color="white" icon size="24" @click="fileCloseHover">
          <v-icon size="20">mdi-close</v-icon>
        </v-btn>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { mapState, mapActions, mapMutations } from 'vuex'
import LibraryFilters from '@/components/library/LibraryFilters.vue'
import LibraryFolder from '@/components/library/LibraryFolder.vue'
import LibraryFile from '@/components/library/LibraryFile.vue'
import LibraryBreadcrumbs from '@/components/library/LibraryBreadcrumbs.vue'
import isStringBase64 from '@/helpers/isStringBase64.js'

export default {
  name: 'MlFilePickerDialog',
  components: {
    LibraryFilters,
    LibraryFolder,
    LibraryFile,
    LibraryBreadcrumbs,
  },
  props: {
    // Array of ProfileIds
    resourcesReceiverIds: {
      type: [Array],
      default: () => [],
    },
    useLibraryResources: {
      type: Boolean,
      default: false,
    },
    showDialog: {
      type: Boolean,
      default: false,
    },
    allowedFileTypes: {
      type: [Array, null],
      default: null,
    },
  },
  data() {
    return {
      dialog: false,
      file: null,
      currentDirectoryId: null,
      searchBy: {
        name: '',
      },
      currentFilter: false,
      savedFilter: false,
      fileHover: false,
      fileHoverUrl: null,
      fileHoverType: 'base64',
      fileHoverTimeout: null,
      fileHoverInterval: null,
      progressBarsValues: {},
    }
  },
  watch: {
    showDialog: function(val) {
      if (val) {
        this.dialog = true
        this.loadDirectory(this.savedDirectory || this.mainFolderId)
      } else {
        this.dialog = false
      }
    },
  },
  computed: {
    ...mapState('resourcesStates', ['resources', 'resource']),
    ...mapState('libraryStates', ['loading', 'directory', 'savedDirectory']),
    ...mapState('userStates', ['mainFolderId', 'profileId']),
    pagination() {
      return (
        this.directory?.pageMetadata || {
          pageNumber: 0,
          pageSize: 50,
          totalPages: 0,
          totalElements: 0,
        }
      )
    },
    folders() {
      if (this.useLibraryResources) return []
      else if (
        !this.useLibraryResources &&
        this.directory.entries &&
        Array.isArray(this.directory.entries)
      )
        return this.directory.entries.filter(x => x.entryTypeId == 12)
      else return []
    },
    files() {
      if (
        this.useLibraryResources &&
        this.resources &&
        Array.isArray(this.resources)
      )
        return this.resources.filter(x => x.entryTypeId == 13)
      else if (
        !this.useLibraryResources &&
        this.directory.entries &&
        Array.isArray(this.directory.entries)
      )
        return this.directory.entries.filter(x => x.entryTypeId == 13)
      else return []
    },
    breadcrumbsFormatted() {
      return this.formatBreadcrumbs()
    },
  },
  created() {},
  methods: {
    isStringBase64,
    ...mapActions('resourcesStates', [
      'searchResources',
      'getResource',
      'likeResource',
      'getResourceById',
    ]),
    ...mapActions('libraryStates', ['getDirectory', 'search']),
    ...mapMutations('libraryStates', ['setSavedDirectory']),
    fileOpenHover(id, thumbnailUrl = false, previewUrl = false) {
      clearTimeout(this.fileHoverTimeout)
      this.fileCloseHover()
      if (id && thumbnailUrl) {
        this.progressBarsValues = { ...this.progressBarsValues, ...{ [id]: 1 } }
        if (this.fileHoverInterval) clearInterval(this.fileHoverInterval)
        this.fileHoverInterval = setInterval(() => {
          if (this.progressBarsValues[id] >= 100) {
            clearInterval(this.fileHoverInterval)
            return
          }
          let tmp = this.progressBarsValues[id]
          this.progressBarsValues = {
            ...this.progressBarsValues,
            ...{ [id]: tmp + 33 },
          }
        }, 200)
        if (this.fileHoverTimeout) clearTimeout(this.fileHoverTimeout)
        this.fileHoverTimeout = setTimeout(() => {
          this.fileHover = null
          if (previewUrl) {
            this.fileHoverUrl = previewUrl // instead of base64 in response new files use now s3 url to get preview
            this.fileHoverType = this.isStringBase64(previewUrl)
              ? 'base64'
              : 'url'
            this.fileHover = true
          } else {
            this.getResourceById({ resourceId: id }).then(response => {
              const { data } = response
              if (data.fileUrl) {
                if (this.fileHover === null) {
                  this.fileHoverUrl = data.previewUrl || data.thumbnailUrl // in case preview is not available
                  this.fileHoverType = this.isStringBase64(
                    data.previewUrl || data.thumbnailUrl,
                  )
                    ? 'base64'
                    : 'url'
                  this.fileHover = true
                }
              }
            })
          }
        }, 1000)
      } else this.fileClearHover(id)
    },
    fileClearHover(id) {
      this.progressBarsValues = { ...this.progressBarsValues, ...{ [id]: 0 } }

      clearInterval(this.fileHoverInterval)
      if (!this.fileHover) {
        clearTimeout(this.fileHoverTimeout)
        this.fileHover = false
        this.fileHoverUrl = null
        this.fileHoverType = 'base64'
      }
    },
    fileCloseHover() {
      this.fileHover = false
    },
    onClickOutsideFileHover() {
      this.fileCloseHover()
    },
    onKeydownFileHover(key) {
      if (key.code === 'Escape' && key.isTrusted) this.onClickOutsideFileHover()
    },
    closeDialog() {
      this.dialog = false
      this.$emit('close-dialog')
    },
    onClickOutside() {
      this.closeDialog()
    },
    onKeydown(key) {
      if (key.code === 'Escape' && key.isTrusted) this.closeDialog()
    },
    onInput({ name, value }) {
      this.file = value
      this.$emit('on-change', this.file)
    },
    onChange(event) {
      this.file = event
      this.$emit('on-change', this.file)
    },
    onSubmit() {
      this.$emit('on-submit', this.file)
      this.closeDialog()
    },
    loadDirectory(id, pageNumber = 0, pageSize = 50) {
      if (this.useLibraryResources) {
        this.searchResources({ params: this.requestQuery({}) })
      } else {
        this.getDirectory({
          id: id,
          mainFolderId: this.mainFolderId,
          pageNumber,
          pageSize,
        }).then(url => {
          this.currentDirectoryId = id
          this.setSavedDirectory(id)
        })
      }
    },
    changeLibraryPage(pageNumber) {
      this.getDirectory({
        id: this.currentDirectoryId,
        mainFolderId: this.mainFolderId,
        pageNumber: pageNumber - 1,
        pageSize: this.pagination.pageSize,
      }).then(url => {
        this.setSavedDirectory(this.currentDirectoryId)
      })
    },
    searchClear() {
      this.currentFilter = false
      this.savedFilter = false
      this.loadDirectory(this.mainFolderId)
    },
    searchInput({ key, value }) {
      const oldFilters = this.savedFilter
        ? this.savedFilter.filter(el => el.key !== key)
        : []
      this.filterBy([...oldFilters, { key: key, value: value }])
    },
    searchRun(test) {
      this.filterBy(test)
    },
    chooseFile(data = {}) {
      const { id, name, ...rest } = data
      this.$emit('file-picked', id, name, rest)
    },
    formatBreadcrumbs() {
      //transform breadcrumbs from nested breadcrumb object (content in content in content...) to simple array
      let items = []
      let b = this.directory.breadcrumb
      if (b) rec(b, true)
      function rec(b, root = false) {
        let item = {}
        item.name = b.name
        item.id = b.id
        if (root) item.root = root
        items.push(item)
        if (b.content && b.content.length > 0) {
          rec(b.content[0])
        }
      }
      return items
    },
    filterBy(filters) {
      let filtersArray = []
      let tmpObj = {}
      if (Array.isArray(filters)) {
        filtersArray = filters
      } else if (typeof filters === 'object' && filters.key) {
        filtersArray = [filters]
      } else {
        filtersArray = false
      }

      if (Array.isArray(filtersArray)) {
        filtersArray.forEach(filter => {
          tmpObj[filter.key] = filter.value
        })
      } else tmpObj = false

      if (tmpObj && Object.keys(tmpObj).length > 0) {
        if (this.useLibraryResources) {
          this.searchResources({ params: this.requestQuery(tmpObj) }).then(
            url => {
              this.currentFilter = filtersArray
              this.savedFilter = filtersArray
            },
          )
        } else {
          this.search({
            directoryId: this.mainFolderId,
            params: this.requestQuery(tmpObj),
          }).then(url => {
            this.currentFilter = filtersArray
            this.savedFilter = filtersArray
          })
        }
      } else {
        this.loadDirectory(this.mainFolderId)
        this.currentFilter = false
        this.savedFilter = false
      }
    },
    requestQuery(filter) {
      /* Add receiver profileIds to query */
      if (this.useLibraryResources) {
        if (!this.resourcesReceiverIds.length)
          filter.receiverProfileIds = [this.profileId]
        else filter.receiverProfileIds = this.resourcesReceiverIds
      }

      let keys = Object.keys(filter).filter(key => filter[key] || filter[key])
      return keys.reduce((acc, key) => {
        acc.append(key, filter[key])
        return acc
      }, new URLSearchParams())
    },
    favoriteResource(id, value) {
      this.likeResource({ resourceId: id, isFavorite: value }).then(() => {
        if (!this.currentFilter) this.loadDirectory(this.currentDirectoryId)
        else {
          this.filterBy(this.savedFilter)
        }
      })
    },
  },
}
</script>

<style lang="scss">
@import '@/styles/variables.scss';

.ml-file-picker {
  &-dialog {
    margin: $gutter;
    margin-left: auto;
    height: 100%;
    max-height: calc(100% - #{2 * $gutter}) !important;

    &__file-hover-dialog {
      width: auto !important;
      padding: 10px;
      background: $bh-white;
      .v-btn {
        position: absolute;
        top: 10px;
        right: 10px;
        width: 32px;
        height: 32px;
        background: $bh-blue;
      }
      img {
        display: block;
        max-height: 80vh;
        max-width: 1000px;
      }
    }

    > .v-card {
      width: 100%;
      height: 100%;
    }
    .library {
      background: $bh-white;
      padding: 0 0.75 * $gutter;
      height: calc(100% - 76px);
      overflow: auto;
      &-breadcrumbs {
      }
      &-heading {
        color: $bh-gray--2;
        font-size: 1rem;
        padding-left: 8px;
      }
      &-folders {
        display: flex;
        flex-wrap: wrap;
      }
      &-files {
        display: flex;
        flex-wrap: wrap;
      }
    }

    .library-pagination {
      min-height: 100px;

      .v-pagination__item {
        margin-top: 0;
        margin-bottom: 0;
      }
    }
  }
  &-toolbar-title {
    color: $bh-blue;
    font-size: 1.5715rem;
    font-weight: bold;
  }
  &-card {
    &-title {
      font-size: $font-size-root;
      color: $bh-gray--2;
      padding-left: 1.5 * $gutter;
      padding-right: 1.5 * $gutter;
    }
    &-container {
      padding-left: 1.5 * $gutter;
      padding-right: 1.5 * $gutter;
    }
    &-footer {
      padding: 1.5 * $gutter;
    }
  }
  &-form {
    background-color: $bh-blue--4;
    height: 100%;
    position: relative;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    overflow: auto;

    &-body {
      ::v-deep .v-toolbar__content {
        padding-left: 1.5 * $gutter;
        padding-right: 1.5 * $gutter;
      }
    }
    &-col-left {
      padding-top: 0;
      padding-right: 0.25 * $gutter;
    }
    &-col-right {
      padding-top: 0;
      padding-left: 0.25 * $gutter;
    }
  }
}
</style>
